import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { formatDateOnly, todayInput } from "@/lib/date";

export const CHIP = {
  present: "bg-emerald-100 text-emerald-700",
  tardy: "bg-amber-100 text-amber-700",
  absent: "bg-red-100 text-red-700",
  excused: "bg-sky-100 text-sky-700",
};

// Green ≥ 90%, amber 75–89%, red < 75%.
export const rateTone = (r) => (r == null ? "text-slate-400" : r >= 90 ? "text-emerald-600" : r >= 75 ? "text-amber-600" : "text-red-600");

export const HISTORY_LINK = "/member/schedule?tab=attendance";

// Same math as the backend summary: excused sits outside the "Attended X of Y" total.
export function summarize(records) {
  const c = { present: 0, tardy: 0, absent: 0, excused: 0 };
  records.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
  return { ...c, total: c.present + c.tardy + c.absent, attended: c.present + c.tardy };
}

export function AttendanceCounts({ s, testPrefix = "member-attendance" }) {
  return (
    <>
      <p className="text-sm font-semibold text-slate-700 tabular-nums" data-testid={`${testPrefix}-attended`}>Attended {s.attended} of {s.total}</p>
      <div className="mt-3 flex flex-wrap gap-1.5 text-[11px] font-semibold">
        <span className={`rounded-full px-2 py-0.5 ${CHIP.present}`} data-testid={`${testPrefix}-present`}>{s.present} Present</span>
        <span className={`rounded-full px-2 py-0.5 ${CHIP.tardy}`} data-testid={`${testPrefix}-tardy`}>{s.tardy} Tardy</span>
        <span className={`rounded-full px-2 py-0.5 ${CHIP.absent}`} data-testid={`${testPrefix}-absent`}>{s.absent} Absent</span>
        {s.excused > 0 && <span className={`rounded-full px-2 py-0.5 ${CHIP.excused}`} data-testid={`${testPrefix}-excused`}>{s.excused} Excused</span>}
      </div>
    </>
  );
}

export function RecordRow({ r, testId }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2.5" data-testid={testId}>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-slate-800">{r.event_title || "Event"}</p>
        <p className="text-xs text-slate-500 tabular-nums">{formatDateOnly(r.event_date)}</p>
        {r.note && <p className="mt-0.5 text-xs italic text-slate-500" data-testid={`${testId}-note`}>“{r.note}”</p>}
      </div>
      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${CHIP[r.status] || CHIP.present}`}>{r.status}</span>
    </div>
  );
}

// YYYY-MM-DD `days` before `ymd` (calendar math in UTC to avoid DST drift).
const ymdMinus = (ymd, days) => {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
};

// Member dashboard: only shown when there's an absence or tardy in the last 7 days (today + 6 prior).
export default function MemberAttendanceCard({ endpoint = "/attendance/my", to = HISTORY_LINK }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(endpoint).then(({ data: d }) => setData(d)).catch(() => {}); }, [endpoint]);
  if (!data || data.enabled === false) return null;
  const today = todayInput();
  const since = ymdMinus(today, 6);
  const recent = data.records.filter((r) => ["absent", "tardy"].includes(r.status) && r.event_date >= since && r.event_date <= today);
  if (!recent.length) return null;
  return (
    <Link to={to} className="block" data-testid="member-attendance-link">
      <Card className="mt-4 p-5 border-slate-200 shadow-sm rounded-xl transition-transform hover:-translate-y-0.5 hover:shadow-md animate-fade-in-up" data-testid="member-attendance-card">
        <div className="mb-4 flex items-center justify-between gap-2">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500"><CalendarCheck className="h-4 w-4" /> Attendance</p>
          <ChevronRight className="h-4 w-4 text-slate-300" />
        </div>
        <div className="grid gap-5 sm:grid-cols-[auto_1fr]">
          <div className="sm:pr-6 sm:border-r sm:border-slate-100"><AttendanceCounts s={data.summary} /></div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-500">Tardies & absences · last 7 days</p>
            <div className="divide-y divide-slate-100" data-testid="member-attendance-recent">
              {recent.map((r, i) => <RecordRow key={`${r.event_id}-${i}`} r={r} testId={`member-attendance-recent-${r.event_id}`} />)}
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}
